import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Default delay applied when an explicit `delay` is not passed. 300 ms matches
 * the widget card popover timing in the design spec (design §5 Decision #8).
 */
export const HOVER_DEBOUNCE_MS = 300 as const;

export interface UseHoverWithDebounceOptions {
  /** Override the default 300 ms delay. */
  delay?: number;
}

export interface UseHoverWithDebounceReturn {
  /** True only after the pointer has stayed over for `delay` ms. */
  isHovered: boolean;
  /** Attach to the target element's `onMouseEnter`. */
  onMouseEnter: () => void;
  /** Attach to the target element's `onMouseLeave`. */
  onMouseLeave: () => void;
}

/**
 * Tracks a debounced hover state: `isHovered` becomes `true` only when the
 * pointer has remained over the target for `delay` ms continuously. A leave
 * (or another enter) before the window elapses cancels the pending flip.
 *
 * SSR-safe — no `window` access at import time. Uses `setTimeout` /
 * `clearTimeout`, which are defined in both browser and jsdom environments at
 * the time the hook actually runs (i.e. inside a React effect/callback).
 */
export function useHoverWithDebounce(
  options?: UseHoverWithDebounceOptions
): UseHoverWithDebounceReturn {
  const { delay = HOVER_DEBOUNCE_MS } = options ?? {};

  const [isHovered, setIsHovered] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPending = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const onMouseEnter = useCallback(() => {
    clearPending();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setIsHovered(true);
    }, delay);
  }, [clearPending, delay]);

  const onMouseLeave = useCallback(() => {
    clearPending();
    setIsHovered(false);
  }, [clearPending]);

  // Cancel any pending timer when the consumer unmounts.
  useEffect(() => clearPending, [clearPending]);

  return { isHovered, onMouseEnter, onMouseLeave };
}
